import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Text, TextInput, Theme, Button, StyledTable, StyledTableCell } from '@simplybenefits/sb-ui-library';
import { dispatchGetAccountNotes, createAccountNote } from '../../../actions/accounts.js';
import * as dates from "@simplybenefits/sb-dates";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";

/**
 * A component that displays and allows adding renewal notes for a specific account and renewal.
 * @param {string} accountID 
 * @param {string} renewalID 
 * @param {Function} dispatch
 */
const RenewalNotes = ({ accountID, renewalID, renewalDate, dispatch }) => {
    const [ selectedNote, setSelectedNote ] = useState(null);
    const [ notes, setNotes ] = useState(null);
    const [ showNoteBox, setShowNoteBox ] = useState(false);
    const [ newNote, setNewNote ] = useState('');

    useEffect(() => {

        /** */
        const setRenewalNotes = async () => {
            const classID = null;
            const divisionID = null;
            const getRenewalsNotes = true;
            const fetchedNotes = await dispatch(dispatchGetAccountNotes(accountID, classID, divisionID, getRenewalsNotes));
            setNotes(fetchedNotes);
        };

        setRenewalNotes();
    }, []);

    /**
     * Toggles the visibility of the selected note's full content.
     * @param {Object} note
     */
    const _toggleNote = (note) => note !== selectedNote ? setSelectedNote(note) : setSelectedNote(null);

    /**
     * Truncates a note's body if it exceeds 50 characters.
     * @param {string} body 
     * @returns {string}
     */
    const _truncate = (body) => body.length > 50 ? `${ body.substring(0, 50) }...` : body;

    /**
     * Saves the newly created note to the account's renewal notes.
     * @returns {Promise<void>}
     */
    const _saveNote = async () => {
        const classID = null;
        const divisionID = null;
        const note = await dispatch(createAccountNote(accountID, classID, divisionID, renewalID, newNote));
        setNotes((prevNotes) => [{ ...note, renewalDate }, ...prevNotes ]);
        setShowNoteBox(false);
        setNewNote('');
    };

    const styles = {
        sectionHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '30px',
            marginBottom: '10px'
        },
        tableHeading: {
            paddingTop: "10px",
            paddingBottom: "10px"
        }
    };

    return (
        <>
            <div style={ styles.sectionHeader }>
                <Text customStyle={{ fontSize: 19 }} bold>
                    Notes
                </Text>
                {
                    !showNoteBox &&
                        <Button small bold transparent onClick={ () => setShowNoteBox(true)}>
                            Add Note
                        </Button>
                }
            </div>

            {
                showNoteBox &&
                    <>
                        <TextInput
                            fullWidth
                            value={ newNote }
                            variant="outlined"
                            onChange={(event) => setNewNote(event.target.value)}
                            multiline={ true }
                            rows={ 5 }
                            backgroundColor={ Theme.colors.surface}
                            placeholder={ 'Add Renewal Note...' }
                        />
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 22 }}>
                            <Button transparent onClick={ () => setShowNoteBox(false)}>
                                Close
                            </Button>
                            <Button transparent onClick={ () => _saveNote()}>
                                Save
                            </Button>
                        </div>
                    </>
            }

            <StyledTable>
                <TableHead>
                    <TableRow sx={{ backgroundColor: Theme.colors.skyBlue }}>
                        <StyledTableCell style={styles.tableHeading}>
                            Note
                        </StyledTableCell>

                        <StyledTableCell style={styles.tableHeading}>
                            Author
                        </StyledTableCell>

                        <StyledTableCell style={styles.tableHeading}>
                            Created
                        </StyledTableCell>

                        <StyledTableCell style={styles.tableHeading}>
                            Renewal
                        </StyledTableCell>

                        <StyledTableCell style={styles.tableHeading} />
                    </TableRow>
                </TableHead>

                <TableBody>
                    {
                        notes && notes.length > 0 ? 
                            notes.map((note, index) => {
                                let backgroundObject = { border: "none" };
                                if (index % 2 !== 0) {
                                    backgroundObject = { backgroundColor: Theme.colors.backgroundGrey, border: "none" };
                                }

                                return (
                                    <React.Fragment key={note.accNoteID}>
                                        <TableRow style={{ ...backgroundObject }}>
                                            <StyledTableCell >
                                                { _truncate(note.body) }
                                            </StyledTableCell>

                                            <StyledTableCell >
                                                { note.createdBy }
                                            </StyledTableCell>

                                            <StyledTableCell >
                                                { dates.formatDateForDisplay(note.dateAdded, 'lll') }
                                            </StyledTableCell>

                                            <StyledTableCell >
                                                { dates.formatDateForDisplay(note.renewalDate, 'll') } 
                                            </StyledTableCell>

                                            <StyledTableCell style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end' }}>
                                                <Button transparent onClick={() => _toggleNote(note)}>
                                                    { selectedNote?.accNoteID === note.accNoteID ? 'Hide Full' : 'View Full' }
                                                </Button>      
                                            </StyledTableCell>
                                        </TableRow>

                                        {
                                            selectedNote?.accNoteID === note.accNoteID &&
                                            <TableRow style={{ height: 44.5, ...backgroundObject }}>
                                                <StyledTableCell colSpan={5}>
                                                    <Text bold>
                                                        {note.body}
                                                    </Text>
                                                </StyledTableCell>
                                            </TableRow>
                                        }
                                    </React.Fragment>
                                );
                            })
                            :
                            <TableRow>
                                <StyledTableCell>
                                    <Text medium color={Theme.colors.mainTextGrey} margin="10px 0px 0px 0px">
                                        No Notes Available.
                                    </Text>
                                </StyledTableCell>
                            </TableRow>
                    }
                </TableBody>
            </StyledTable>
        </> 
    );
};

export default (connect)()(RenewalNotes);